import React, { useCallback, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade } from 'swiper'
import styled from 'styled-components'
import { SwapLeftOutlined, SwapRightOutlined } from '@ant-design/icons'

import 'swiper/css'
import 'swiper/css/effect-fade'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyImage } from '~/components/ui/MyImage'
import { FenToYuan } from '~/utils'
import {
  getRecommendProductList_itemModule_productList_edges_node_artworkList,
  ArtworkDBType,
} from '~/graphqls/types'
import Link from 'next/link'
import CircleAndSquare from '~/components/icon/CircleAndSquare'
import { AiRankIcon } from '~/components/icon/AiRank'
import { getProductPrice } from '~/components/pages/product/common'

const StyledCommendModule = styled.div`
  .recommend-thumb-wrap {
    position: relative;
    width: 640px;
    height: 700px;
    border-radius: 16px;
    overflow: hidden;
    &:hover {
      .recommend-thumb {
        transform: scale(1.05);
        transition: all ease-in-out 180ms;
      }
    }
  }
  .recommend-thumb {
    height: 100%;
    width: 100%;
    transition: all ease-in-out 180ms;
  }
  .recommend-tag {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1;
    display: flex;
    align-items: center;
    .recommend-tag-item {
      font-size: 12px;
      line-height: 12px;
      padding: 7px 8px;
      border-radius: 4px;
      color: ${props => props.theme.light3};
      background: rgba(0, 0, 0, 0.56);
      margin-right: 8px;
      display: inline-flex;
      align-items: center;
    }
  }
  .recommend-body {
    width: 480px;
    padding-left: 128px;
  }
  .recommend-title {
    font-size: 64px;
    line-height: 64px;
    font-weight: 600;
    margin-bottom: 20px;
    word-break: break-all;
  }
  .recommend-info {
    display: flex;
    margin-bottom: 40px;
  }
  .recommend-creator {
    display: flex;
  }

  .creator-thumb {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-position: center;
    background-size: cover;
  }
  .creator-body {
    margin-left: 8px;
    max-width: 150px;
  }
  .creator-label,
  .product-label {
    font-size: 12px;
    line-height: 20px;
    color: ${props => props.theme.dark4};
  }
  .creator-name,
  .product-price {
    font-size: 14px;
    line-height: 24px;
    color: ${props => props.theme.dark2};
  }
  .recommend-product {
    display: flex;
    margin-left: 32px;
  }
  .product-body {
    margin-left: 8px;
  }
  .recommend-switch {
    display: flex;
    margin-top: 40px;
  }
  .switch-btn {
    border-width: 2px;
    border-style: solid;
    color: ${props => props.theme.dark4};
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    &.switch-btn-prev {
      border-color: rgba(0, 0, 0, 0);
    }
    &.switch-btn-next {
      margin-left: 16px;
      border-color: ${props => props.theme.light1};
    }
  }
`

const StyledSwiper = styled(Swiper)`
  .swiper-slide {
    display: flex;
    align-items: center;
    background: ${props => props.theme.light3};
  }
`

export const RecommendModule: React.FC<{
  data:
    | (getRecommendProductList_itemModule_productList_edges_node_artworkList | null)[]
    | null
}> = ({ data }) => {
  const swiperRef = useRef<any>(null)

  const onPrev = useCallback(() => {
    swiperRef.current?.children?.[0]?.swiper?.slidePrev()
  }, [])

  const onNext = useCallback(() => {
    swiperRef.current?.children?.[0]?.swiper?.slideNext()
  }, [])

  const onEventReport = (
    item: getRecommendProductList_itemModule_productList_edges_node_artworkList | null
  ) => {
    window._hmt?.push(['_trackEvent', '今日推荐', 'click', item?.name, 1])
  }

  return (
    <StyledCommendModule ref={swiperRef}>
      <StyledSwiper effect={'fade'} modules={[EffectFade]} loop={true}>
        {data?.map(item => {
          // 获取价格等内容
          const aiPrice = getProductPrice(item)
          return (
            <SwiperSlide key={item?.id}>
              <div className="recommend-thumb-wrap">
                <Link href={`/product/${item?.id}`}>
                  <a onClick={() => onEventReport(item)}>
                    <MyImage
                      tag="v800"
                      src={item?.artworkUrl}
                      className="recommend-thumb"
                    />
                    <div className="recommend-tag">
                      {!!item?.existItem && (
                        <span className="recommend-tag-item">
                          <CircleAndSquare /> <span className="ml-1">文创</span>
                        </span>
                      )}
                    </div>
                    {item?.aiArtwork?.extend?.competitionRank && (
                      <span className="absolute w-12 h-12 top-1 right-1">
                        <AiRankIcon
                          rank={item?.aiArtwork?.extend?.competitionRank}
                          className="w-full h-full"
                          textClassName="pt-[10px]"
                        />
                      </span>
                    )}
                  </a>
                </Link>
              </div>
              <div className="recommend-body">
                <Link href={`/product/${item?.id}`}>
                  <a onClick={() => onEventReport(item)}>
                    <div className="recommend-title">{item?.name}</div>
                  </a>
                </Link>

                <div className="recommend-info">
                  <div className="recommend-creator">
                    <MyImage
                      tag="v400"
                      className="creator-thumb"
                      src={item?.creator?.avatar}
                    />
                    <div className="creator-body">
                      <div className="creator-label">创作者</div>
                      <div className="creator-name">
                        <Link href={`/user/${item?.creator?.id}`}>
                          <a>{item?.creator?.name}</a>
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="recommend-product">
                    <div className="product-thumb">
                      <img src="/images/icons/copyright.svg" alt="" />
                    </div>
                    <div className="product-body">
                      {item?.type === ArtworkDBType.AI_COPYRIGHT ? (
                        <>
                          <div className="product-label">{aiPrice.maxName}</div>
                          <div className="product-price">
                            ¥ {FenToYuan(aiPrice.max)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="product-label">版权所有权</div>
                          <div className="product-price">
                            ¥ {FenToYuan(item?.buy?.[0]?.price)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <MyBtn block type="primary">
                  <Link href={`/product/${item?.id}`}>
                    <a onClick={() => onEventReport(item)}>立即购买</a>
                  </Link>
                </MyBtn>
                {data.length > 1 && (
                  <div className="recommend-switch">
                    <div
                      className="switch-btn switch-btn-prev"
                      onClick={onPrev}
                    >
                      <SwapLeftOutlined style={{ fontSize: 24 }} />
                    </div>
                    <div
                      className="switch-btn switch-btn-next"
                      onClick={onNext}
                    >
                      <SwapRightOutlined style={{ fontSize: 24 }} />
                    </div>
                  </div>
                )}
              </div>
            </SwiperSlide>
          )
        })}
      </StyledSwiper>
    </StyledCommendModule>
  )
}
